import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilterPersistence } from './useFilterPersistence';

export interface UseListPageStateOpts<FKey extends string> {
  /** localStorage/sessionStorage namespace (eg 'products-list') */
  pageKey: string;
  /** Filter keys that sync with URL searchParams (may include 'search') */
  filterKeys: readonly FKey[];
  /** User id for per-user filter persistence scope */
  userId?: string;
  /** Initial page (default 1) */
  defaultPage?: number;
  /** Initial page size (default 20) */
  defaultPageSize?: number;
  /** Default filters when URL and sessionStorage are empty */
  defaultFilters?: Partial<Record<FKey, string>>;
}

export interface TablePatch {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'ascend' | 'descend' | null;
}

export interface UseListPageStateReturn<FKey extends string> {
  /** True after saved filters have been restored into the URL. */
  filtersReady: boolean;
  // Table state
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 'ascend' | 'descend';
  setTableState: (patch: TablePatch) => void;

  // Applied filters (from URL)
  filters: Record<FKey, string>;

  // Draft filters (staged in UI, not yet applied)
  drafts: Record<FKey, string>;
  setDraft: (key: FKey, value: string) => void;

  // Actions
  applyFilters: () => void;
  clearFilters: () => void;

  // Meta
  activeFilterCount: number;

  // Row selection
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: (keys: React.Key[]) => void;
  clearSelection: () => void;
}

function readFilters<FKey extends string>(
  params: URLSearchParams,
  filterKeys: readonly FKey[],
): Record<FKey, string> {
  const out = {} as Record<FKey, string>;
  for (const k of filterKeys) {
    out[k] = params.get(k) ?? '';
  }
  return out;
}

export function useListPageState<FKey extends string>(
  opts: UseListPageStateOpts<FKey>,
): UseListPageStateReturn<FKey> {
  const { pageKey, filterKeys, userId, defaultPage = 1, defaultPageSize = 20, defaultFilters } = opts;
  const [searchParams, setSearchParams] = useSearchParams();

  const { saveSnapshot, clearSnapshot, isReady: filtersReady } = useFilterPersistence(
    pageKey,
    userId,
    filterKeys as unknown as string[],
    defaultFilters as Record<string, string>,
  );

  // Applied filters read from URL
  const filters = useMemo(
    () => readFilters(searchParams, filterKeys),
    [searchParams, filterKeys],
  );

  // Draft state mirrors URL at mount and on URL changes (eg. back/forward)
  const [drafts, setDrafts] = useState<Record<FKey, string>>(() =>
    readFilters(searchParams, filterKeys),
  );
  const draftsRef = useRef(drafts);

  useEffect(() => {
    const nextDrafts = readFilters(searchParams, filterKeys);
    draftsRef.current = nextDrafts;
    setDrafts(nextDrafts);
  }, [searchParams.toString()]);

  const setDraft = useCallback((key: FKey, value: string) => {
    draftsRef.current = { ...draftsRef.current, [key]: value };
    setDrafts(draftsRef.current);
  }, []);

  const applyFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    const latestDrafts = draftsRef.current;
    for (const k of filterKeys) {
      const v = latestDrafts[k]?.trim?.() ?? latestDrafts[k];
      if (v) next.set(k, v);
      else next.delete(k);
    }
    next.set('page', '1');
    setSearchParams(next, { replace: true });
    saveSnapshot(next);
  }, [searchParams, filterKeys, setSearchParams, saveSnapshot]);

  const clearFilters = useCallback(() => {
    const cleared = {} as Record<FKey, string>;
    for (const k of filterKeys) cleared[k] = '';
    draftsRef.current = cleared;
    setDrafts(cleared);

    const next = new URLSearchParams(searchParams);
    for (const k of filterKeys) next.delete(k);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
    clearSnapshot();
  }, [filterKeys, searchParams, setSearchParams, clearSnapshot]);

  // Table state
  const page = Number(searchParams.get('page') ?? defaultPage);
  const pageSize = Number(searchParams.get('pageSize') ?? defaultPageSize);
  const sortField = searchParams.get('sortField') ?? undefined;
  const sortOrder = (searchParams.get('sortOrder') as 'ascend' | 'descend' | null) ?? undefined;

  const setTableState = useCallback(
    (patch: TablePatch) => {
      const next = new URLSearchParams(searchParams);
      if (patch.page !== undefined) next.set('page', String(patch.page));
      if (patch.pageSize !== undefined) next.set('pageSize', String(patch.pageSize));
      if (patch.sortField !== undefined) {
        if (patch.sortField) next.set('sortField', patch.sortField);
        else next.delete('sortField');
      }
      if (patch.sortOrder !== undefined) {
        if (patch.sortOrder) next.set('sortOrder', patch.sortOrder);
        else next.delete('sortOrder');
      }
      setSearchParams(next, { replace: true });
      // Trang/pageSize/sort cũng phải vào snapshot, nếu không thì vào detail rồi
      // quay ra sẽ nhảy về trang 1 với pageSize mặc định.
      saveSnapshot(next);
    },
    [searchParams, setSearchParams, saveSnapshot],
  );

  // Active filter count
  const activeFilterCount = useMemo(
    () => filterKeys.reduce((sum, k) => sum + (filters[k] ? 1 : 0), 0),
    [filters, filterKeys],
  );

  // Row selection
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const clearSelection = useCallback(() => setSelectedRowKeys([]), []);

  return {
    filtersReady,
    page,
    pageSize,
    sortField,
    sortOrder,
    setTableState,
    filters,
    drafts,
    setDraft,
    applyFilters,
    clearFilters,
    activeFilterCount,
    selectedRowKeys,
    setSelectedRowKeys,
    clearSelection,
  };
}
